import { z } from 'zod';

export function isValidNumber(
  value: unknown,
  options: { positive?: boolean; required?: boolean } = { positive: false, required: false }
): boolean {
  if (typeof value === 'number') return true;
  if (typeof value !== 'string') return false;

  if (!options.required && value === '') return true;

  const num = parseInt(value);
  if (isNaN(num)) return false;
  if (options.positive && num < 0) return false;

  return true;
}

export function isPositiveNumber(value: unknown): boolean {
  return isValidNumber(value, { positive: true });
}

export function isValidLatitude(value: unknown): boolean {
  const num = Number(value);
  return !isNaN(num) && num >= -90 && num <= 90;
}

export function isValidLongitude(value: unknown): boolean {
  const num = Number(value);
  return !isNaN(num) && num >= -180 && num <= 180;
}

export const PHONE_REGEX = /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/;

export function isValidPhoneNumber(value: string): boolean {
  return PHONE_REGEX.test(value);
}

export function hasMinLength(min: number) {
  return (value: string) => value.length >= min;
}

export function hasMaxLength(max: number) {
  return (value: string) => value.length <= max;
}

export function isWithinLength(min: number, max: number) {
  return (value: string) => value.length >= min && value.length <= max;
}

export const ValidationMessages = {
  required: (field: string) => `${field} is required.`,
  minLength: (field: string, min: number) => `${field} must be at least ${min} characters.`,
  maxLength: (field: string, max: number) =>
    `${field} must not be longer than ${max} characters.`,
  invalidNumber: (field: string) => `${field} must be a valid number.`,
  positiveNumber: (field: string) => `${field} must be a positive number.`,
  invalidEmail: 'Please enter a valid email address.',
  invalidPhone: 'Please enter a valid phone number.',
  invalidLatitude: 'Latitude must be between -90 and 90 degrees.',
  invalidLongitude: 'Longitude must be between -180 and 180 degrees.',
} as const;

export const createPositiveNumberStringSchema = ({
  fieldName,
  required = true,
}: {
  fieldName: string;
  required?: boolean;
}) => {
  if (required) {
    return z
      .string({ error: ValidationMessages.required(fieldName) })
      .refine(
        (v) => isValidNumber(v, { required }),
        ValidationMessages.invalidNumber(fieldName)
      )
      .refine(isPositiveNumber, ValidationMessages.positiveNumber(fieldName));
  }

  return z
    .string({ error: ValidationMessages.required(fieldName) })
    .refine(isValidNumber, ValidationMessages.invalidNumber(fieldName))
    .refine(isPositiveNumber, ValidationMessages.positiveNumber(fieldName))
    .optional();
};
